import { createReducer, on } from '@ngrx/store';

import { VddDetailsListActions } from './vdd-details-list.actions';
import { RawVddDetails } from './vdd-details-list.model';
import { VddDetailsListState, VddLoading } from './vdd-details-list.state';
import { VddDetailsListHelper } from './vdd-details-list.helper';

export const initVddDetailsListState: VddDetailsListState = {
  vddDetailsList: [],
  writeAttempts: 0,
  writingLoading: null,
};

export function createEmptyVdd(vddName: string, logicalLinkName: string): RawVddDetails {
  const vdd: RawVddDetails = {
    vddName,
    logicalLinkName,
    descriptionKey: 'TI_' + vddName,
    metadata: null,
    metadataLoading: false,
    metadataError: null,
    structure: null,
    structureLoading: false,
    structureError: null,
    singleRead: null,
    singleReadLoading: false,
    singleReadError: null,
    flattenedMetadata: null,
    flattenedSingleRead: null,
    flattenedStructure: null,
  };

  return vdd;
}

export function matchVdd(
  vdd: RawVddDetails | VddLoading,
  vddNameToMatch: string,
  logicalLinkNameToMatch: string
): boolean {
  return vdd.vddName === vddNameToMatch && vdd.logicalLinkName === logicalLinkNameToMatch;
}

export const vddDetailsListReducer = createReducer(
  initVddDetailsListState,

  on(
    VddDetailsListActions.ClearVdd,
    (state, { vddName, logicalLinkName }): VddDetailsListState => {
      const vddExist = state.vddDetailsList.find((vdd) => matchVdd(vdd, vddName, logicalLinkName));
      if (vddExist) {
        return {
          ...state,
          vddDetailsList: state.vddDetailsList.map((vddDetails) => {
            if (vddDetails.vddName === vddName && vddDetails.logicalLinkName === logicalLinkName) {
              return {
                ...vddDetails,
                descriptionKey: 'TI_' + vddName,
                logicalLinkName,
                metadata: null,
                metadataLoading: false,
                metadataError: null,
                flattenedMetadata: null,
                structure: null,
                structureLoading: false,
                structureError: null,
                flattenedStructure: null,
                singleRead: null,
                singleReadLoading: false,
                singleReadError: null,
                flattenedSingleRead: null,
              };
            }

            return vddDetails;
          }),
        };
      }

      return {
        ...state,
        vddDetailsList: state.vddDetailsList.concat(createEmptyVdd(vddName, logicalLinkName)),
      };
    }
  ),

  on(
    VddDetailsListActions.GetVddMetadataAttempt,
    (state, { vddName, logicalLinkName }): VddDetailsListState => {
      const vddExist = state.vddDetailsList.find((vdd) => matchVdd(vdd, vddName, logicalLinkName));
      if (vddExist) {
        return {
          ...state,
          vddDetailsList: state.vddDetailsList.map((vddDetails) => {
            if (matchVdd(vddDetails, vddName, logicalLinkName)) {
              return {
                ...vddDetails,
                metadata: null,
                metadataLoading: true,
                metadataError: null,
                flattenedMetadata: null,
              };
            }

            return vddDetails;
          }),
        };
      }

      return {
        ...state,
        vddDetailsList: state.vddDetailsList.concat(createEmptyVdd(vddName, logicalLinkName)),
      };
    }
  ),

  on(
    VddDetailsListActions.GetVddMetadataSuccess,
    (state, { vddName, logicalLinkName, metadata }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            metadata,
            metadataLoading: false,
            metadataError: null,
            logicalLinkName,
            flattenedMetadata: VddDetailsListHelper.getFlattenedMetadata(metadata),
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.GetVddMetadataFailed,
    (state, { vddName, logicalLinkName, error }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            metadata: null,
            metadataLoading: false,
            metadataError: error,
            flattenedMetadata: null,
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.GetVddStructureAttempt,
    (state, { vddName, logicalLinkName }): VddDetailsListState => {
      const vddExist = state.vddDetailsList.find((vdd) => matchVdd(vdd, vddName, logicalLinkName));
      if (vddExist) {
        return {
          ...state,
          vddDetailsList: state.vddDetailsList.map((vddDetails) => {
            if (matchVdd(vddDetails, vddName, logicalLinkName)) {
              return {
                ...vddDetails,
                structure: null,
                structureLoading: true,
                structureError: null,
                flattenedStructure: null,
              };
            }

            return vddDetails;
          }),
        };
      }

      return {
        ...state,
        vddDetailsList: state.vddDetailsList.concat(createEmptyVdd(vddName, logicalLinkName)),
      };
    }
  ),

  on(
    VddDetailsListActions.GetVddStructureSuccess,
    (state, { vddName, logicalLinkName, structure }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            structure,
            structureLoading: false,
            structureError: null,
            logicalLinkName,
            flattenedStructure: VddDetailsListHelper.getFlattenedStructure(structure),
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.GetVddStructureFailed,
    (state, { vddName, logicalLinkName, error }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            structure: null,
            structureLoading: false,
            structureError: error,
            flattenedStructure: null,
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.GetVddSingleValueAttempt,
    (state, { vddName, logicalLinkName }): VddDetailsListState => {
      const vddExist = state.vddDetailsList.find((vdd) => matchVdd(vdd, vddName, logicalLinkName));
      if (vddExist) {
        return {
          ...state,
          vddDetailsList: state.vddDetailsList.map((vddDetails) => {
            if (matchVdd(vddDetails, vddName, logicalLinkName)) {
              return {
                ...vddDetails,
                singleRead: null,
                singleReadLoading: true,
                singleReadError: null,
                flattenedSingleRead: null,
              };
            }

            return vddDetails;
          }),
        };
      }

      return {
        ...state,
        vddDetailsList: state.vddDetailsList.concat(createEmptyVdd(vddName, logicalLinkName)),
      };
    }
  ),

  on(
    VddDetailsListActions.GetVddSingleValueSuccess,
    (state, { vddName, logicalLinkName, singleRead }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            singleRead,
            singleReadLoading: false,
            singleReadError: null,
            logicalLinkName,
            flattenedSingleRead: VddDetailsListHelper.getFlattenedSingleRead(singleRead),
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.GetVddSingleValueFailed,
    (state, { vddName, logicalLinkName, error }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            singleRead: null,
            singleReadLoading: false,
            singleReadError: error,
            flattenedSingleRead: null,
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.writeVddSingleValue.attempt,
    (state, { vddName, logicalLink }): VddDetailsListState => ({
      ...state,
      writingLoading: { vddName, loading: true, logicalLinkName: logicalLink },
    })
  ),

  on(
    VddDetailsListActions.writeVddSingleValue.failed,
    (state): VddDetailsListState => {
      return {
        ...state,
        writingLoading: null,
      };
    }
  ),

  on(
    VddDetailsListActions.verifyWrittenVddSingleValue.successfulVerification,
    (state): VddDetailsListState => ({
      ...state,
      writingLoading: null,
    })
  ),

  on(
    VddDetailsListActions.verifyWrittenVddSingleValue.failedVerification,
    (state, { vddName, logicalLink, oldSingleRead }): VddDetailsListState => {
      return {
        ...state,
        writingLoading: null,
        vddDetailsList: state.vddDetailsList.map((vddDetails) => {
          if (matchVdd(vddDetails, vddName, logicalLink)) {
            return { ...vddDetails, singleRead: oldSingleRead, loading: false };
          }

          return vddDetails;
        }),
      };
    }
  ),

  on(
    VddDetailsListActions.verifyWrittenVddSingleValue.error,
    (state, { vddName, logicalLinkName, error }): VddDetailsListState => ({
      ...state,
      writingLoading: null,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLinkName)) {
          return {
            ...vddDetails,
            singleRead: null,
            singleReadLoading: false,
            singleReadError: error,
            flattenedSingleRead: null,
          };
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.setDefaultStateOnTabChange,
    (): VddDetailsListState => initVddDetailsListState
  ),

  on(
    VddDetailsListActions.SetNewValue,
    (state, { vddName, logicalLink, path, newValue }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLink)) {
          if (vddDetails?.singleRead) {
            return {
              ...vddDetails,
              singleRead: VddDetailsListHelper.getUpdatedSingleReadValues(
                path,
                newValue,
                vddDetails.singleRead
              ),
            };
          } else {
            return {
              ...vddDetails,
              payloadForVddIsReadFalse: VddDetailsListHelper.getPayloadForVddIsReadFalse(
                path,
                newValue,
                vddDetails?.structure,
                vddDetails?.payloadForVddIsReadFalse
              ),
            };
          }
        }

        return vddDetails;
      }),
    })
  ),

  on(
    VddDetailsListActions.SetRawSingleReadValues,
    (state, { vddName, logicalLink, rawSingleReadValues }): VddDetailsListState => ({
      ...state,
      vddDetailsList: state.vddDetailsList.map((vddDetails) => {
        if (matchVdd(vddDetails, vddName, logicalLink)) {
          return {
            ...vddDetails,
            singleRead: rawSingleReadValues,
          };
        }

        return vddDetails;
      }),
    })
  )
);
